import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';


// ─── auth helper ─────────────────────────────────────────────────────────────

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

// ─── role helpers ─────────────────────────────────────────────────────────────

const ROLE_RANK = { owner: 3, manager: 2, staff: 1 };

const ROLE_STYLES = {
  owner:   { color: '#166534', background: '#dcfce7' },
  manager: { color: '#1e40af', background: '#dbeafe' },
  staff:   { color: '#374151', background: '#f3f4f6' },
};

function canRemove(myRole, targetRole) {
  // owner can remove managers and staff (not other owners)
  // manager can remove staff only
  if (myRole === 'owner')   return targetRole !== 'owner';
  if (myRole === 'manager') return targetRole === 'staff';
  return false;
}

// ─── sub-components ───────────────────────────────────────────────────────────

function Spinner({ size = 40, color = '#f97316' }) {
  return (
    <>
      <div style={{
        width: size,
        height: size,
        border: `4px solid rgba(0,0,0,0.08)`,
        borderTop: `4px solid ${color}`,
        borderRadius: '50%',
        animation: 'hl-spin 0.9s linear infinite',
        margin: '0 auto',
      }} />
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

function RoleBadge({ role }) {
  const { t } = useTranslation();
  if (!role) return null;
  const s = ROLE_STYLES[role] || ROLE_STYLES.staff;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: s.color,
      background: s.background,
      flexShrink: 0,
    }}>
      {t(`organizer.role_${role}`, { defaultValue: role })}
    </span>
  );
}

function AvatarCircle({ name }) {
  const initials = (name || '?')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div style={{
      width: 44,
      height: 44,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #fbbf24, #f97316)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 700,
      fontSize: 16,
      flexShrink: 0,
      userSelect: 'none',
    }}>
      {initials}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// ─── Staff Card ───────────────────────────────────────────────────────────────

function StaffCard({ member, myRole, myUserId, onRemoved }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  const showRemove = (
    canRemove(myRole, member.role) &&
    member.userId !== myUserId
  );

  async function handleRemove() {
    setRemoving(true);
    setRemoveError('');
    try {
      const token = getAuthToken();
      const base = getApiBaseUrl();
      const res = await fetch(
        `${base}/venue-dashboard/${member._venueId}/staff/${member.userId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      onRemoved(member.userId);
    } catch (err) {
      setRemoveError(err.message || t('vd.removeFailed'));
      setRemoving(false);
      setConfirming(false);
    }
  }

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      padding: '16px 18px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      marginBottom: 12,
    }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <AvatarCircle name={member.name} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>
              {member.name || t('vd.unknown')}
            </span>
            <RoleBadge role={member.role} />
          </div>
          {member.email && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {member.email}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            {t('vd.joined')} {formatDate(member.joinedAt)}
          </div>
        </div>
        {showRemove && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            style={{
              background: 'none',
              border: '1px solid #fca5a5',
              color: '#dc2626',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: 600,
              flexShrink: 0,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.target.style.background = '#fef2f2'}
            onMouseLeave={e => e.target.style.background = 'none'}
          >
            {t('vd.remove')}
          </button>
        )}
      </div>

      {/* Inline confirm */}
      {confirming && (
        <div style={{
          marginTop: 14,
          padding: '12px 14px',
          background: '#fef2f2',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#7f1d1d', fontWeight: 600, flex: 1 }}>
            {t('vd.areYouSureUndone')}
          </span>
          <button
            onClick={handleRemove}
            disabled={removing}
            style={{
              background: removing ? '#f9a8a8' : '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '7px 16px',
              fontSize: 13,
              fontWeight: 700,
              cursor: removing ? 'default' : 'pointer',
            }}
          >
            {removing ? t('vd.removing') : t('vd.yesRemove')}
          </button>
          <button
            onClick={() => { setConfirming(false); setRemoveError(''); }}
            disabled={removing}
            style={{
              background: '#fff',
              border: '1px solid #d1d5db',
              color: '#374151',
              borderRadius: 8,
              padding: '7px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('vd.cancel')}
          </button>
        </div>
      )}

      {removeError && (
        <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>
          {removeError}
        </div>
      )}
    </div>
  );
}

// ─── Pending Invites Section ──────────────────────────────────────────────────

function PendingInvitesSection({ invites }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const count = invites.length;

  return (
    <div style={{ marginBottom: 24 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          fontSize: 15,
          fontWeight: 700,
          color: '#111',
          textAlign: 'left',
        }}
      >
        <span>{t('vd.pendingInvitesSection')} ({count})</span>
        <span style={{ fontSize: 18, color: '#9ca3af', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          ▾
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {count === 0 ? (
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: '18px',
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: 14,
            }}>
              {t('vd.noPendingInvites')}
            </div>
          ) : (
            invites.map((inv, i) => (
              <div key={inv.token || i} style={{
                background: '#fff',
                borderRadius: 12,
                padding: '14px 18px',
                marginBottom: 8,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Show name/email for venue_member_invites; fall back to token tail for legacy venue_invites */}
                  {(inv.invitedName || inv.invitedEmail) ? (
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inv.invitedName || inv.invitedEmail}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#6b7280', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      …{(inv.token || '').slice(-12)}
                    </div>
                  )}
                  {inv.invitedEmail && inv.invitedName && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inv.invitedEmail}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>
                    {t('vd.expires')} {formatDate(inv.expiresAt)}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <RoleBadge role={inv.role} />
                  <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600, background: '#fef3c7', padding: '2px 8px', borderRadius: 10 }}>
                    {t('vd.pending')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Create Invite Section ────────────────────────────────────────────────────

function CreateInviteSection({ venueId, myRole }) {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState('staff');
  const [loading, setLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  const canPickManager = myRole === 'owner';

  async function handleGenerate() {
    setLoading(true);
    setError('');
    setInviteUrl('');
    setExpiresAt('');
    try {
      const token = getAuthToken();
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/venue-dashboard/${venueId}/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: selectedRole }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setInviteUrl(data.inviteUrl || '');
      setExpiresAt(data.expiresAt || '');
    } catch (err) {
      setError(err.message || t('vd.failedToGenerateInvite'));
    } finally {
      setLoading(false);
    }
  }

  function handleCopy() {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = inviteUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }

  const roleToggleStyle = (role) => ({
    flex: 1,
    padding: '10px 0',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    cursor: role === 'manager' && !canPickManager ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s, color 0.15s',
    background: selectedRole === role ? 'linear-gradient(135deg, #fbbf24, #f97316)' : '#f3f4f6',
    color: selectedRole === role ? '#fff' : (role === 'manager' && !canPickManager ? '#d1d5db' : '#374151'),
    opacity: role === 'manager' && !canPickManager ? 0.5 : 1,
  });

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      padding: '20px 18px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#111' }}>
        {t('vd.createNewInviteLink')}
      </h3>

      {/* Role toggle */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
          {t('vd.inviteRoleLabel')}
        </div>
        <div style={{
          display: 'flex',
          gap: 8,
          background: '#f3f4f6',
          borderRadius: 10,
          padding: 4,
        }}>
          <button
            onClick={() => setSelectedRole('staff')}
            style={roleToggleStyle('staff')}
          >
            {t('organizer.role_staff')}
          </button>
          <button
            onClick={() => canPickManager && setSelectedRole('manager')}
            disabled={!canPickManager}
            title={!canPickManager ? t('vd.onlyOwnersCanInviteManagers') : ''}
            style={roleToggleStyle('manager')}
          >
            {t('organizer.role_manager')}
          </button>
        </div>
        {!canPickManager && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
            {t('vd.managersCanInviteStaff')}
          </div>
        )}
      </div>

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={loading}
        style={{
          width: '100%',
          padding: '13px 0',
          background: loading
            ? '#fed7aa'
            : 'linear-gradient(135deg, #fbbf24, #f97316, #fbbf24)',
          backgroundSize: '200% auto',
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          fontSize: 15,
          fontWeight: 700,
          cursor: loading ? 'default' : 'pointer',
          letterSpacing: '0.02em',
          boxShadow: loading ? 'none' : '0 2px 8px rgba(249,115,22,0.3)',
          transition: 'box-shadow 0.2s',
        }}
      >
        {loading ? t('vd.generating') : t('vd.generateInviteLink')}
      </button>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: 12,
          padding: '10px 14px',
          background: '#fef2f2',
          borderRadius: 8,
          fontSize: 13,
          color: '#dc2626',
        }}>
          {error}
        </div>
      )}

      {/* Result */}
      {inviteUrl && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            {t('vd.inviteLinkGenerated')}
          </div>
          <div style={{
            background: '#f3f4f6',
            borderRadius: 10,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <div style={{
              flex: 1,
              fontSize: 13,
              color: '#374151',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              lineHeight: 1.5,
            }}>
              {inviteUrl}
            </div>
            <button
              onClick={handleCopy}
              style={{
                flexShrink: 0,
                background: copied ? '#dcfce7' : '#fff',
                border: `1px solid ${copied ? '#86efac' : '#d1d5db'}`,
                borderRadius: 8,
                padding: '7px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                color: copied ? '#166534' : '#374151',
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {copied ? t('vd.copied') : '📋 ' + t('vd.copyLink')}
            </button>
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
            {t('vd.expiresIn7Days')}
            {expiresAt ? ` (${formatDate(expiresAt)})` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function VenueDashboardStaffPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [status, setStatus] = useState('loading'); // loading | auth | error | ready
  const [errorMsg, setErrorMsg] = useState('');
  const [venueId, setVenueId] = useState('');
  const [venueName, setVenueName] = useState('');
  const [myRole, setMyRole] = useState('');
  const [myUserId, setMyUserId] = useState('');
  const [staff, setStaff] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  const load = useCallback(async () => {
    setStatus('loading');
    setErrorMsg('');
    const token = getAuthToken();
    if (!token) {
      setStatus('auth');
      return;
    }
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/venue-dashboard/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        setStatus('auth');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const vid = data.venue?.id || data.venue?._id || '';
      setVenueId(vid);
      setVenueName(data.venue?.name || '');
      setMyRole(data.myRole || 'staff');
      setMyUserId(data.myUserId || data.userId || '');

      // Attach venueId to each staff member so StaffCard can call the delete endpoint
      const staffList = (data.staff || []).map(m => ({ ...m, _venueId: vid }));
      setStaff(staffList);
      setPendingInvites(data.pendingInvites || []);
      setStatus('ready');
    } catch (err) {
      setErrorMsg(err.message || t('vd.failedToLoadStaffData'));
      setStatus('error');
    }
  }, [slug, t]);

  useEffect(() => { load(); }, [load]);

  function handleStaffRemoved(userId) {
    setStaff(prev => prev.filter(m => m.userId !== userId));
  }

  // ── Header ─────────────────────────────────────────────────────────────────

  const header = (
    <div style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      height: 64,
      background: 'linear-gradient(135deg, #fbbf24, #f97316, #fbbf24)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      boxShadow: '0 2px 8px rgba(249,115,22,0.25)',
    }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          borderRadius: 8,
          color: '#fff',
          fontSize: 18,
          width: 38,
          height: 38,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-label={t('vd.back')}
      >
        ←
      </button>
      <h1 style={{
        flex: 1,
        textAlign: 'center',
        color: '#fff',
        fontSize: 17,
        fontWeight: 700,
        margin: 0,
        letterSpacing: '0.01em',
      }}>
        {t('vd.staffInvites')}
      </h1>
      {/* spacer to balance back button */}
      <div style={{ width: 38 }} />
    </div>
  );

  // ── Loading ────────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {header}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', gap: 16 }}>
          <Spinner />
          <div style={{ color: '#9ca3af', fontSize: 14 }}>{t('vd.loadingStaff')}</div>
        </div>
      </div>
    );
  }

  // ── Auth error ─────────────────────────────────────────────────────────────

  if (status === 'auth') {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {header}
        <div style={{ maxWidth: 440, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111', marginBottom: 8 }}>{t('vd.signInRequired')}</h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
            {t('vd.signInToManage')}
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'linear-gradient(135deg, #fbbf24, #f97316)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '12px 32px',
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('vd.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // ── Generic error ──────────────────────────────────────────────────────────

  if (status === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {header}
        <div style={{ maxWidth: 440, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111', marginBottom: 8 }}>{t('vd.somethingWentWrong')}</h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
            {errorMsg || t('vd.couldNotLoadStaffDataTryAgain')}
          </p>
          <button
            onClick={load}
            style={{
              background: 'linear-gradient(135deg, #fbbf24, #f97316)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '12px 32px',
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('vd.retry')}
          </button>
        </div>
      </div>
    );
  }

  // ── Ready ──────────────────────────────────────────────────────────────────

  const canManageInvites = myRole === 'owner' || myRole === 'manager';

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {header}

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px 40px' }}>

        {/* Venue name subtitle */}
        {venueName && (
          <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20, textAlign: 'center' }}>
            {venueName}
          </div>
        )}

        {/* ── Section 1: Current staff ─────────────────────────────────── */}
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 14, marginTop: 0 }}>
          {t('vd.teamMembers')}
          <span style={{ fontWeight: 400, fontSize: 14, color: '#9ca3af', marginLeft: 8 }}>
            ({staff.length})
          </span>
        </h2>

        {staff.length === 0 ? (
          <div style={{
            background: '#fff',
            borderRadius: 14,
            padding: '28px 18px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 14,
            marginBottom: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            {t('vd.noTeamMembers')}
          </div>
        ) : (
          <div style={{ marginBottom: 24 }}>
            {staff.map(member => (
              <StaffCard
                key={member.userId}
                member={member}
                myRole={myRole}
                myUserId={myUserId}
                onRemoved={handleStaffRemoved}
              />
            ))}
          </div>
        )}

        {/* ── Section 2: Pending invites ───────────────────────────────── */}
        <PendingInvitesSection invites={pendingInvites} />

        {/* ── Section 3: Create invite ─────────────────────────────────── */}
        {canManageInvites && (
          <CreateInviteSection venueId={venueId} myRole={myRole} />
        )}
      </div>
      <DashboardNavBar />
    </div>
  );
}
